import React, {
  FunctionComponent as FC,
  useEffect,
  useState,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import { actionCreators } from "../../../../state";
import { State } from "../../../../state";
import "./index.scss";

// components
import Panel from "../../common/Panel";
import SubTitle from "../../common/SubTitle";
import BarChart from "../../common/Visualization/BarChart";
import { Spin, Switch, ConfigProvider, Form, Input } from "antd";

// types
import { impactEntityViewStyle } from "../../../../types/style.global";
import { GlobalStyle } from "../../../../types/style.d";
import { GrantViewTypes, InteractionTypes, MainViewTypes } from "../../../../types/data";
import { ImpactType, ClinicalTrialImpactAttr, EntityType } from "../../../../types/data.global";

// utils
import _ from "lodash";
import ChartUnit from "./ChartUnit";


interface ImpactEntityViewProps {
  viewParam: GlobalStyle.ViewSize;
  panelParam: GlobalStyle.PanelStyle;
  viewName: string;
}

const ImpactEntityView: FC<ImpactEntityViewProps> = (props) => {
  const { viewParam, panelParam, viewName } = props;

  const { Search } = Input;

  const dispatch = useDispatch()
  const {
    getImpactEntityViewDirectPaperData,
    getImpactEntityViewDirectPatentData,
    getImpactEntityViewDirectClinicalTrialData,
    getImpactEntityViewDirectClinicalTrialTypeData,
    getImpactEntityViewIndirectPatentData,
    getImpactEntityViewIndirectClinicalTrialData,
    getImpactEntityViewIndirectClinicalTrialTypeData,
    getImpactEntityViewIndirectPolicyData,
    getImpactEntityViewIndirectPolicySourceCountryData,
    getImpactEntityViewIndirectPolicySourceTitleData,
    getImpactEntityViewIndirectNewsfeedData,

    searchImpactEntityViewPaperData,
    searchImpactEntityViewPatentData,
    searchImpactEntityViewClinicalTrialData,
    searchImpactEntityViewPolicyData,

    updateImpactEntityViewLoading,
  } = bindActionCreators(actionCreators, dispatch)

  // data reducer
  const grantIDList: string[] = useSelector((state: State) => state.dataReducer?.grantIDList) as string[]
  const impactEntityPaperDirectData: GrantViewTypes.BarChartDataUnit[] = useSelector((state: State) => state.dataReducer?.impactEntityPaperDirectData) as GrantViewTypes.BarChartDataUnit[]
  const impactEntityPatentDirectData: GrantViewTypes.BarChartDataUnit[] = useSelector((state: State) => state.dataReducer?.impactEntityPatentDirectData) as GrantViewTypes.BarChartDataUnit[]
  const impactEntityClinicalTrialPhaseDirectData: GrantViewTypes.BarChartDataUnit[] = useSelector((state: State) => state.dataReducer?.impactEntityClinicalTrialPhaseDirectData) as GrantViewTypes.BarChartDataUnit[]
  const impactEntityClinicalTrialTypeDirectData: GrantViewTypes.BarChartDataUnit[] = useSelector((state: State) => state.dataReducer?.impactEntityClinicalTrialTypeDirectData) as GrantViewTypes.BarChartDataUnit[]
  const impactEntityPatentIndirectData: GrantViewTypes.BarChartDataUnit[] = useSelector((state: State) => state.dataReducer?.impactEntityPatentIndirectData) as GrantViewTypes.BarChartDataUnit[]
  const impactEntityClinicalTrialPhaseIndirectData: GrantViewTypes.BarChartDataUnit[] = useSelector((state: State) => state.dataReducer?.impactEntityClinicalTrialPhaseIndirectData) as GrantViewTypes.BarChartDataUnit[]
  const impactEntityClinicalTrialTypeIndirectData: GrantViewTypes.BarChartDataUnit[] = useSelector((state: State) => state.dataReducer?.impactEntityClinicalTrialTypeIndirectData) as GrantViewTypes.BarChartDataUnit[]
  const impactEntityPolicyIndirectData: GrantViewTypes.BarChartDataUnit[] = useSelector((state: State) => state.dataReducer?.impactEntityPolicyIndirectData) as GrantViewTypes.BarChartDataUnit[]
  const impactEntityPolicySourceCountryIndirectData: GrantViewTypes.BarChartDataUnit[] = useSelector((state: State) => state.dataReducer?.impactEntityPolicySourceCountryIndirectData) as GrantViewTypes.BarChartDataUnit[]
  const impactEntityPolicySourceTitleIndirectData: GrantViewTypes.BarChartDataUnit[] = useSelector((state: State) => state.dataReducer?.impactEntityPolicySourceTitleIndirectData) as GrantViewTypes.BarChartDataUnit[]
  const impactEntityNewsfeedIndirectData: GrantViewTypes.BarChartDataUnit[] = useSelector((state: State) => state.dataReducer?.impactEntityNewsfeedIndirectData) as GrantViewTypes.BarChartDataUnit[]
  const highlghtedGrantFieldNode: MainViewTypes.HighlightedGrantFieldNode = useSelector((state: State) => state.dataReducer?.highlghtedGrantFieldNode) as MainViewTypes.HighlightedGrantFieldNode
  const impactEntityPaperDirectGrantIdList: string[] = useSelector((state: State) => state.dataReducer?.impactEntityPaperDirectGrantIdList) as string[]
  const impactEntityPatentDirectGrantIdList: string[] = useSelector((state: State) => state.dataReducer?.impactEntityPatentDirectGrantIdList) as string[]
  const impactEntityClinicalTrialDirectGrantIdList: string[] = useSelector((state: State) => state.dataReducer?.impactEntityClinicalTrialDirectGrantIdList) as string[]
  const impactEntityPatentIndirectGrantIdList: string[] = useSelector((state: State) => state.dataReducer?.impactEntityPatentIndirectGrantIdList) as string[]
  const impactEntityClinicalTrialIndirectGrantIdList: string[] = useSelector((state: State) => state.dataReducer?.impactEntityClinicalTrialIndirectGrantIdList) as string[]
  const impactEntityPolicyIndirectGrantIdList: string[] = useSelector((state: State) => state.dataReducer?.impactEntityPolicyIndirectGrantIdList) as string[]
  const impactEntityNewsfeedIndirectGrantIdList: string[] = useSelector((state: State) => state.dataReducer?.impactEntityNewsfeedIndirectGrantIdList) as string[]

  // ui reducer
  const isImpactEntityViewLoading: boolean = useSelector((state: State) => state.dataReducer?.isImpactEntityViewLoading) as boolean
  const impactType: string = useSelector((state: State) => state.uiReducer?.impactType) as string
  const updatedImpactEntityType: string = useSelector((state: State) => state.uiReducer?.updatedImpactEntityType) as string
  const entityTopicNodeDict: InteractionTypes.UIParamEntityTopicNodeDict = useSelector((state: State) => state.uiReducer?.entityTopicNodeDict) as InteractionTypes.UIParamEntityTopicNodeDict
  const fieldLevelRange: InteractionTypes.UIParamFieldLevelRangeDict = useSelector((state: State) => state.uiReducer?.fieldLevelRange) as InteractionTypes.UIParamFieldLevelRangeDict
  const selectedImpactEntityField: { [entity: string]: string } = useSelector((state: State) => state.uiReducer?.selectedImpactEntityField) as { [entity: string]: string }

  // state variables
  const [isDirectImpactDisplay, setDirectImpactDisplay] = useState("block");
  const [isIndirectImpactDisplay, setIndirectImpactDisplay] = useState("none");
  const [isPaperTemporalVisible, setIsPaperTemporalVisible] = useState(false);
  const [isPatentTemporalVisible, setIsPatentTemporalVisible] = useState(false);
  const [isClinicalTrialTemporalVisible, setIsClinicalTrialTemporalVisible] = useState(false);
  const [isClinicalTrialTypeTemporalVisible, setIsClinicalTrialTypeTemporalVisible] = useState(false);
  const [isIndirectPatentTemporalVisible, setIsIndirectPatentTemporalVisible] = useState(false);
  const [isIndirectClinicalTrialTemporalVisible, setIsIndirectClinicalTrialTemporalVisible] = useState(false);
  const [isIndirectClinicalTrialTypeTemporalVisible, setIsIndirectClinicalTrialTypeTemporalVisible] = useState(false);
  const [isPolicyTemporalVisible, setIsPolicyTemporalVisible] = useState(false);
  const [isPolicySourceCountryTemporalVisible, setIsPolicySourceCountryTemporalVisible] = useState(false);
  const [isPolicySourceTitleTemporalVisible, setIsPolicySourceTitleTemporalVisible] = useState(false);
  const [isNewsfeedTemporalVisible, setIsNewsfeedTemporalVisible] = useState(false);
  const [clinicalTrialImpactAttr, setClinicalTrialImpactAttr] = useState<ClinicalTrialImpactAttr>(ClinicalTrialImpactAttr.PHASE);
  const [indirectClinicalTrialImpactAttr, setIndirectClinicalTrialImpactAttr] = useState<ClinicalTrialImpactAttr>(ClinicalTrialImpactAttr.PHASE);
  // loading related
  const [isDirectPaperLoading, setIsDirectPaperLoading] = useState(false);
  const [isDirectPatentLoading, setIsDirectPatentLoading] = useState(false);
  const [isDirectClinicalTrialLoading, setIsDirectClinicalTrialLoading] = useState(false);
  const [isIndirectPatentLoading, setIsIndirectPatentLoading] = useState(false);
  const [isIndirectClinicalTrialLoading, setIsIndirectClinicalTrialLoading] = useState(false);
  const [isIndirectPolicyLoading, setIsIndirectPolicyLoading] = useState(false);
  const [isIndirectNewsfeedLoading, setIsIndirectNewsfeedLoading] = useState(false);


  // const entityLevelDict = impactEntityViewData.entityLevelDict;



  // 1. function to updateImpactEntityView: call backend to update Impact Entity View (upper right view)
  /*
    Description:
      Update the Impact Entity View data.
      Triggered anytime when the grantIDList is updated.
    Input Parameters:
      `grantIDList`: list[str]
    Output:
      xxx
  */
  const updateDirectPaperSubview = async () => {
    setIsDirectPaperLoading(true);
    var entityField = "all";
    if (Object.keys(selectedImpactEntityField).length === 1) {
      // 获取唯一的entity（键）
      const entityType = Object.keys(selectedImpactEntityField)[0];
      if (entityType === EntityType.PAPER){
        // 获取对应的value
        entityField = selectedImpactEntityField[entityType];
        // console.log(entity, value); // 这里可以根据你的需要进行操作
      }
    }
    try {
      const result = await getImpactEntityViewDirectPaperData(grantIDList, entityField, fieldLevelRange['paper'][fieldLevelRange['paper'].length - 1], highlghtedGrantFieldNode.grantFieldID)
    } catch (error) {
      console.error(error);
    } finally {
      setIsDirectPaperLoading(false);
    }
  }

  const updateDirectPatentSubview = async () => {
    setIsDirectPatentLoading(true);
    var entityField = "all";
    if (Object.keys(selectedImpactEntityField).length === 1) {
      // 获取唯一的entity（键）
      const entityType = Object.keys(selectedImpactEntityField)[0];
      if (entityType === EntityType.PATENT){
        // 获取对应的value
        entityField = selectedImpactEntityField[entityType];
        // console.log(entity, value); // 这里可以根据你的需要进行操作
      }
    }
    try {
      // console.log("updateDirectPatentSubview entityLevelDict.patent:", entityLevelDict.patent);
      const result = await getImpactEntityViewDirectPatentData(grantIDList, entityField, fieldLevelRange['patent'][fieldLevelRange['patent'].length - 1], highlghtedGrantFieldNode.grantFieldID)
    } catch (error) {
      console.error(error);
    } finally {
      setIsDirectPatentLoading(false);
    }
  }

  const updateDirectClinicalTrialSubview = async () => {
    setIsDirectClinicalTrialLoading(true);
    var entityField = "all";
    if (Object.keys(selectedImpactEntityField).length === 1) {
      // 获取唯一的entity（键）
      const entityType = Object.keys(selectedImpactEntityField)[0];
      if (entityType === EntityType.CLINICAL_TRIAL){
        // 获取对应的value
        entityField = selectedImpactEntityField[entityType];
        // console.log(entity, value); // 这里可以根据你的需要进行操作
      }
    }
    try {
      const resultPhase = await getImpactEntityViewDirectClinicalTrialData(grantIDList, entityField, fieldLevelRange['clinical_trial'][fieldLevelRange['clinical_trial'].length - 1], highlghtedGrantFieldNode.grantFieldID)
      const resultType = await getImpactEntityViewDirectClinicalTrialTypeData(grantIDList, entityField, fieldLevelRange['clinical_trial'][fieldLevelRange['clinical_trial'].length - 1], highlghtedGrantFieldNode.grantFieldID)
    } catch (error) {
      console.error(error);
    } finally {
      setIsDirectClinicalTrialLoading(false);
    }
  }

  const updateIndirectPatentSubview = async () => {
    setIsIndirectPatentLoading(true);
    var entityType = ""
    var entityField = "all";
    if (Object.keys(selectedImpactEntityField).length === 1) {
      // 获取唯一的entity（键）
      entityType = Object.keys(selectedImpactEntityField)[0];
      if (entityType === EntityType.PATENT){
        // 获取对应的value
        entityField = selectedImpactEntityField[entityType];
        // console.log(entity, value); // 这里可以根据你的需要进行操作
      }
    }
    // console.log("updateIndirectPatentSubview entityType: ", entityType)
    // console.log("updateIndirectPatentSubview entityField: ", entityField)
    try {
      const result = await getImpactEntityViewIndirectPatentData(grantIDList, entityField, fieldLevelRange['patent'][fieldLevelRange['patent'].length - 1], highlghtedGrantFieldNode.grantFieldID)
    } catch (error) {
      console.error(error);
    } finally {
      setIsIndirectPatentLoading(false);
    }
  }

  const updateIndirectClinicalTrialSubview = async () => {
    setIsIndirectClinicalTrialLoading(true);
    var entityField = "all";
    if (Object.keys(selectedImpactEntityField).length === 1) {
      // 获取唯一的entity（键）
      const entityType = Object.keys(selectedImpactEntityField)[0];
      if (entityType === EntityType.CLINICAL_TRIAL){
        // 获取对应的value
        entityField = selectedImpactEntityField[entityType];
        // console.log(entity, value); // 这里可以根据你的需要进行操作
      }
    }
    try {
      const resultPhase = await getImpactEntityViewIndirectClinicalTrialData(grantIDList, entityField, fieldLevelRange['clinical_trial'][fieldLevelRange['clinical_trial'].length - 1], highlghtedGrantFieldNode.grantFieldID)
      const resultType = await getImpactEntityViewIndirectClinicalTrialTypeData(grantIDList, entityField, fieldLevelRange['clinical_trial'][fieldLevelRange['clinical_trial'].length - 1], highlghtedGrantFieldNode.grantFieldID)
    } catch (error) {
      console.error(error);
    } finally {
      setIsIndirectClinicalTrialLoading(false);
    }
  }

  const updateIndirectPolicySubview = async () => {
    setIsIndirectPolicyLoading(true);
    var entityField = "all";
    if (Object.keys(selectedImpactEntityField).length === 1) {
      // 获取唯一的entity（键）
      const entityType = Object.keys(selectedImpactEntityField)[0];
      if (entityType === EntityType.POLICY){
        // 获取对应的value
        entityField = selectedImpactEntityField[entityType];
        // console.log(entity, value); // 这里可以根据你的需要进行操作
      }
    }
    try {
      const resultSourceType = await getImpactEntityViewIndirectPolicyData(grantIDList, entityField, fieldLevelRange['policy'][fieldLevelRange['policy'].length - 1], highlghtedGrantFieldNode.grantFieldID)
      const resultSourceCountry = await getImpactEntityViewIndirectPolicySourceCountryData(grantIDList, entityField, fieldLevelRange['policy'][fieldLevelRange['policy'].length - 1], highlghtedGrantFieldNode.grantFieldID)
      const resultSourceTitle = await getImpactEntityViewIndirectPolicySourceTitleData(grantIDList, entityField, fieldLevelRange['policy'][fieldLevelRange['policy'].length - 1], highlghtedGrantFieldNode.grantFieldID)
    } catch (error) {
      console.error(error);
    } finally {
      setIsIndirectPolicyLoading(false);
    }
  }

  const updateIndirectNewsfeedSubview = async () => {
    setIsIndirectNewsfeedLoading(true);
    var entityField = "all";
    if (Object.keys(selectedImpactEntityField).length === 1) {
      // 获取唯一的entity（键）
      const entityType = Object.keys(selectedImpactEntityField)[0];
      if (entityType === EntityType.NEWSFEED){
        // 获取对应的value
        entityField = selectedImpactEntityField[entityType];
        // console.log(entity, value); // 这里可以根据你的需要进行操作
      }
    }
    try {
      const result = await getImpactEntityViewIndirectNewsfeedData(grantIDList, entityField, fieldLevelRange['newsfeed'][fieldLevelRange['newsfeed'].length - 1], highlghtedGrantFieldNode.grantFieldID)
    } catch (error) {
      console.error(error);
    } finally {
      setIsIndirectNewsfeedLoading(false);
    }
  }

  useEffect(() => {
    if(impactType === ImpactType.DIRECT_IMPACT){
      setDirectImpactDisplay("block");
      setIndirectImpactDisplay("none");
    }
    else{
      setDirectImpactDisplay("none");
      setIndirectImpactDisplay("block");
    }
  }, [impactType])

  useEffect(() => {
    updateImpactEntityViewLoading(false)

    updateDirectPaperSubview()
    updateDirectPatentSubview()
    updateDirectClinicalTrialSubview()
    updateIndirectPatentSubview()
    updateIndirectClinicalTrialSubview()
    updateIndirectPolicySubview()
    updateIndirectNewsfeedSubview()
  }, [grantIDList])

  useEffect(() => {
    updateImpactEntityViewLoading(false)
    var entityType = ""
    if (Object.keys(selectedImpactEntityField).length === 1) {
      // 获取唯一的entity（键）
      entityType = Object.keys(selectedImpactEntityField)[0];
    }
    if(entityType === EntityType.PAPER){
      updateDirectPaperSubview()
    }
    else if(entityType === EntityType.PATENT){
      if(impactType === ImpactType.DIRECT_IMPACT){
        updateDirectPatentSubview()
      }
      else{
        updateIndirectPatentSubview()
      }
    }
    else if(entityType === EntityType.CLINICAL_TRIAL){
      if(impactType === ImpactType.DIRECT_IMPACT){
        updateDirectClinicalTrialSubview()
      }
      else{
        updateIndirectClinicalTrialSubview()
      }
    }

    else if(entityType === EntityType.POLICY){
      updateIndirectPolicySubview()
    }
    else if(entityType === EntityType.NEWSFEED){
      updateIndirectNewsfeedSubview()
    }
  }, [selectedImpactEntityField])


  // 2. handle user interaction to communicate with backend
  // pass (no interactions in this view)


  // 3. event handlers
  const togglePaperBarOrTempView = () => {
    setIsPaperTemporalVisible(!isPaperTemporalVisible);
  };
  const togglePatentBarOrTempView = () => {
    setIsPatentTemporalVisible(!isPatentTemporalVisible);
  };
  const toggleClinicalTrialBarOrTempView = () => {
    setIsClinicalTrialTemporalVisible(!isClinicalTrialTemporalVisible);
  };
  const toggleClinicalTrialTypeBarOrTempView = () => {
    setIsClinicalTrialTypeTemporalVisible(!isClinicalTrialTypeTemporalVisible);
  };
  const toggleIndirectPatentBarOrTempView = () => {
    setIsIndirectPatentTemporalVisible(!isIndirectPatentTemporalVisible);
  };
  const toggleIndirectClinicalTrialBarOrTempView = () => {
    console.log("toggleIndirectClinicalTrialBarOrTempView");
    setIsIndirectClinicalTrialTemporalVisible(!isIndirectClinicalTrialTemporalVisible);
  };
  const toggleIndirectClinicalTrialTypeBarOrTempView = () => {
    console.log("toggleIndirectClinicalTrialTypeBarOrTempView");
    setIsIndirectClinicalTrialTypeTemporalVisible(!isIndirectClinicalTrialTypeTemporalVisible);
  };
  const togglePolicyBarOrTempView = () => {
    console.log("togglePolicyBarOrTempView");
    setIsPolicyTemporalVisible(!isPolicyTemporalVisible);
  };
  const togglePolicySourceCountryBarOrTempView = () => {
    console.log("togglePolicySourceCountryBarOrTempView");
    setIsPolicySourceCountryTemporalVisible(!isPolicySourceCountryTemporalVisible);
  };
  const togglePolicySourceTitleBarOrTempView = () => {
    console.log("togglePolicySourceTitleBarOrTempView");
    setIsPolicySourceTitleTemporalVisible(!isPolicySourceTitleTemporalVisible);
  };
  const toggleNewsfeedBarOrTempView = () => {
    setIsNewsfeedTemporalVisible(!isNewsfeedTemporalVisible);
  };

  const handlePaperImpactExploreAttrChange = (value: string) => {
    console.log(`handlePaperImpactExploreAttrChange selected ${value}`);
  };

  const handlePaperSearchFieldChange = async (value: string) => {
    console.log(`handlePaperSearchFieldChange: ${value}`);
    // setIsDirectPaperLoading(true);
    try {
      const result = await searchImpactEntityViewPaperData(impactEntityPaperDirectGrantIdList, value, fieldLevelRange['paper'][fieldLevelRange['paper'].length - 1])
    } catch (error) {
      console.error(error);
    } finally {
      // setIsDirectPaperLoading(false);
    }
  };

  const handlePatentImpactExploreAttrChange = (value: string) => {
    console.log(`handlePatentImpactExploreAttrChange selected ${value}`);
  };

  const handlePatentSearchFieldChange = async (value: string) => {
    console.log(`handlePatentSearchFieldChange: ${value}`);
    // setIsDirectPatentLoading(true);
    try {
      const result = await searchImpactEntityViewPatentData(impactEntityPatentDirectGrantIdList, value, fieldLevelRange['patent'][fieldLevelRange['patent'].length - 1])
    } catch (error) {
      console.error(error);
    } finally {
      // setIsDirectPatentLoading(false);
    }
  };

  const handleClinicalTrialImpactExploreAttrChange = (value: string) => {
    console.log(`handleClinicalTrialImpactExploreAttrChange selected ${value}`);
    setClinicalTrialImpactAttr(value as ClinicalTrialImpactAttr);
  };

  const handleClinicalTrialSearchFieldChange = async (value: string) => {
    console.log(`handleClinicalTrialSearchFieldChange: ${value}`);
    // setIsDirectClinicalTrialLoading(true);
    try {
      const result = await searchImpactEntityViewClinicalTrialData(impactEntityClinicalTrialDirectGrantIdList, value, fieldLevelRange['clinical_trial'][fieldLevelRange['clinical_trial'].length - 1])
    } catch (error) {
      console.error(error);
    } finally {
      // setIsDirectClinicalTrialLoading(false);
    }
  };

  const handleIndirectPatentImpactExploreAttrChange = (value: string) => {
    console.log(`handleIndirectPatentImpactExploreAttrChange selected ${value}`);
  };

  const handleIndirectPatentSearchFieldChange = async (value: string) => {
    console.log(`handleIndirectPatentSearchFieldChange: ${value}`);
    // setIsDirectPatentLoading(true);
    try {
      const result = await searchImpactEntityViewPatentData(impactEntityPatentIndirectGrantIdList, value, fieldLevelRange['patent'][fieldLevelRange['patent'].length - 1])
    } catch (error) {
      console.error(error);
    } finally {
      // setIsDirectPatentLoading(false);
    }
  };

  const handleIndirectClinicalTrialImpactExploreAttrChange = (value: string) => {
    console.log(`handleIndirectClinicalTrialImpactExploreAttrChange selected ${value}`);
    setIndirectClinicalTrialImpactAttr(value as ClinicalTrialImpactAttr);
  };

  const handleIndirectClinicalTrialSearchFieldChange = async (value: string) => {
    console.log(`handleIndirectClinicalTrialSearchFieldChange: ${value}`);
    // setIsDirectClinicalTrialLoading(true);
    try {
      const result = await searchImpactEntityViewClinicalTrialData(impactEntityClinicalTrialIndirectGrantIdList, value, fieldLevelRange['clinical_trial'][fieldLevelRange['clinical_trial'].length - 1])
    } catch (error) {
      console.error(error);
    } finally {
      // setIsDirectClinicalTrialLoading(false);
    }
  };

  const handlePolicyImpactExploreAttrChange = (value: string) => {
    console.log(`handlePolicyImpactExploreAttrChange selected ${value}`);
  };

  const handlePolicySearchFieldChange = async (value: string) => {
    console.log(`handlePolicySearchFieldChange: ${value}`);
    // setIsIndirectPolicyLoading(true);
    try {
      const result = await searchImpactEntityViewPolicyData(impactEntityPolicyIndirectGrantIdList, value, fieldLevelRange['policy'][fieldLevelRange['policy'].length - 1])
    } catch (error) {
      console.error(error);
    } finally {
      // setIsIndirectPolicyLoading(false);
    }
  };

  const handleNewsfeedImpactExploreAttrChange = (value: string) => {
    console.log(`handleNewsfeedImpactExploreAttrChange selected ${value}`);
  };

  const handleNewsfeedSearchFieldChange = (value: string) => {
    console.log(`handleNewsfeedSearchFieldChange: ${value}`);
  };


  // 4. visualization
  const barChartParentDivRef = useRef<HTMLDivElement>(null);
  const [barChartParentDivSize, setBarChartParentDivSize] = useState({ width: 0, height: 0 });
  const [barChartWidth, setBarChartWidth] = useState(0);
  const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (!barChartParentDivRef.current) return;

    const handleResize = (entries: ResizeObserverEntry[]) => {
      // Clear any existing timeout
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }

      // Debounce the resize handling
      resizeTimeoutRef.current = setTimeout(() => {
        if (entries.length === 0) return;

        const entry = entries[0];
        const cr = entry.contentRect;
        const newWidth = cr.width;
        const newHeight = cr.height;

        // Only update if the size actually changed significantly (avoid micro-changes)
        const threshold = 1; // 1px threshold
        const currentSize = barChartParentDivSize;

        if (Math.abs(newWidth - currentSize.width) > threshold ||
            Math.abs(newHeight - currentSize.height) > threshold) {

          // Use requestAnimationFrame to batch DOM updates
          requestAnimationFrame(() => {
            setBarChartParentDivSize({ width: newWidth, height: newHeight });
            // const minDiameter = Math.min(newWidth, newHeight)
            // setRadarChartDiameter(minDiameter * (0.24/0.35))
            // setDonutChartDiameter(minDiameter)
            // setRadarChartDiameter(minDiameter * 0.24)
            // setDonutChartDiameter(minDiameter * 0.35)
            setBarChartWidth(Math.max(0, cr.width));
          });
        }
      }, 16); // ~60fps throttling
    };

    // Handle ResizeObserver loop errors
    const ro = new ResizeObserver((entries) => {
      try {
        handleResize(entries);
      } catch (error) {
        // Suppress ResizeObserver loop errors - these are usually benign
        if (error instanceof Error && error.message.includes('ResizeObserver loop')) {
          console.warn('ResizeObserver loop detected, suppressing error');
          return;
        }
        console.error('ResizeObserver error:', error);
      }
    });

    ro.observe(barChartParentDivRef.current);

    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      ro.disconnect();
    };
  }, [barChartParentDivSize.width, barChartParentDivSize.height]);

  // const barChartWidth = impactEntityViewStyle.barChart.width
  const barChartHeight = impactEntityViewStyle.barChart.height
  const barChartWithLabelHeight = impactEntityViewStyle.barChart.height + impactEntityViewStyle.barChart.labelMarginBottom
  const paperBarChart: JSX.Element = <BarChart width={barChartWidth} height={barChartHeight} heightWithLabel={barChartWithLabelHeight} chartTitle="Paper" data={impactEntityPaperDirectData} />
  const patentBarChart: JSX.Element = <BarChart width={barChartWidth} height={barChartHeight} heightWithLabel={barChartWithLabelHeight} chartTitle="Patent" data={impactEntityPatentDirectData} />
  const clinicalTrialPhaseBarChart: JSX.Element = <BarChart width={barChartWidth} height={barChartHeight} heightWithLabel={barChartWithLabelHeight} chartTitle="Clinical-Trial-Phase" data={impactEntityClinicalTrialPhaseDirectData} />
  const clinicalTrialTypeBarChart: JSX.Element = <BarChart width={barChartWidth} height={barChartHeight} heightWithLabel={barChartWithLabelHeight} chartTitle="Clinical-Trial-Type" data={impactEntityClinicalTrialTypeDirectData} />
  const indirectPatentBarChart: JSX.Element = <BarChart width={barChartWidth} height={barChartHeight} heightWithLabel={barChartWithLabelHeight} chartTitle="Patent" data={impactEntityPatentIndirectData} />
  const indirectClinicalTrialPhaseBarChart: JSX.Element = <BarChart width={barChartWidth} height={barChartHeight} heightWithLabel={barChartWithLabelHeight} chartTitle="Clinical-Trial-Phase" data={impactEntityClinicalTrialPhaseIndirectData} />
  const indirectClinicalTrialTypeBarChart: JSX.Element = <BarChart width={barChartWidth} height={barChartHeight} heightWithLabel={barChartWithLabelHeight} chartTitle="Clinical-Trial-Type" data={impactEntityClinicalTrialTypeIndirectData} />
  const policyBarChart: JSX.Element = <BarChart width={barChartWidth} height={barChartHeight} heightWithLabel={barChartWithLabelHeight} chartTitle="Policy" data={impactEntityPolicyIndirectData} />
  const policySourceCountryBarChart: JSX.Element = <BarChart width={barChartWidth} height={barChartHeight} heightWithLabel={barChartWithLabelHeight} chartTitle="Policy" data={impactEntityPolicySourceCountryIndirectData} />
  const policySourceTitleBarChart: JSX.Element = <BarChart width={barChartWidth} height={barChartHeight} heightWithLabel={barChartWithLabelHeight} chartTitle="Policy" data={impactEntityPolicySourceTitleIndirectData} />
  const newsfeedBarChart: JSX.Element = <BarChart width={barChartWidth} height={barChartHeight} heightWithLabel={barChartWithLabelHeight} chartTitle="Newsfeed" data={impactEntityNewsfeedIndirectData} />


  return (
    <Panel
      viewTitle={viewName}
      width={viewParam.width}
      height={viewParam.height}
      margin={panelParam.margin}
      padding={panelParam.padding}
      panelHeaderHeight={panelParam.panelHeaderHeight}
    >
      <ConfigProvider
        theme={{
          token:{
            colorPrimary: '#7F5539',
          },
          components: {
            Form: {
              labelFontSize: 14,
              itemMarginBottom: 0,
            },
          }
        }}
      >
      <Spin tip="Loading" spinning={isImpactEntityViewLoading} size='large'>
          <div className="impact-entity-view" ref={barChartParentDivRef}>
            <div className="direct-impact-entity-subview" style={{display: isDirectImpactDisplay}}>
              <div className="paper-part">
                <SubTitle title="Paper" />
                <div className="search-part">
                  <Form
                    size="middle"
                    labelAlign="left"
                    layout="horizontal"
                    style={{ maxWidth: 300 }}
                  >
                    <div className="field-type-div">Topic Level Attributes: Paper Field</div>
                    {/* <Form.Item label="Impact Explorer Attributes" labelCol={{ span: 15 }} wrapperCol={{ span: 6 }}>
                      <Select
                        defaultValue="Paper Field"
                        style={{ width: 125, height: 25 }}
                        onChange={handlePaperImpactExploreAttrChange}
                        options={[
                          {
                            value: 'Paper Field',
                            label: 'Paper Field',
                          },
                          {
                            value: 'Test',
                            label: 'Test',
                          }
                        ]}
                      />
                    </Form.Item> */}
                    {/* <Form.Item label="Search Field" labelCol={{ span: 8 }} wrapperCol={{ span: 13 }}>
                      <Search
                        placeholder="Search doc by keyword"
                        allowClear
                        size="small"
                        onSearch={handlePaperSearchFieldChange}
                        style={{ width: 215, height: 20 }}
                      />
                    </Form.Item> */}
                  </Form>
                </div>
                <Spin tip="Loading" spinning={isDirectPaperLoading} size='small'>
                  <ChartUnit title="University" isTemporalVisible={isPaperTemporalVisible} toggleTemporalView={togglePaperBarOrTempView} chart={paperBarChart} />
                  {/* <div className="chart-part">
                    <div className="title-switch-part">
                      <div className="title">University</div>
                      <div className="temporal-switch">
                        <div className="temporal-text">Temporal</div>
                        <Switch checked={isPaperTemporalVisible} size="small" onChange={togglePaperBarOrTempView} />
                      </div>
                    </div>
                      <div style={{ position: 'relative' }}>
                        {isPaperTemporalVisible ? (
                          <div className="temporal-view" style={{ position: 'absolute', height: "80px", width: "324px"}}>
                            This is the Temporal View.
                          </div>
                        ) : (
                          <div className="barchart-view" style={{ position: 'absolute', height: "80px", width: "324px" }}>
                            {paperBarChart}
                          </div>
                        )}
                      </div>
                  </div> */}
                </Spin>
              </div>
              <div className="patent-part">
                <SubTitle title="Patent" />
                <div className="search-part">
                  <Form
                    size="middle"
                    labelAlign="left"
                    layout="horizontal"
                    style={{ maxWidth: 300 }}
                  >
                    <div className="field-type-div">Topic Level Attributes: CPC Category</div>
                    {/* <Form.Item label="Impact Explorer Attributes" labelCol={{ span: 15 }} wrapperCol={{ span: 6 }}>
                      <Select
                        defaultValue="CPC Category"
                        style={{ width: 125, height: 25 }}
                        onChange={handlePatentImpactExploreAttrChange}
                        options={[
                          {
                            value: 'CPC Category',
                            label: 'CPC Category',
                          },
                          {
                            value: 'Test',
                            label: 'Test',
                          }
                        ]}
                      />
                    </Form.Item> */}
                    {/* <Form.Item label="Search Field" labelCol={{ span: 8 }} wrapperCol={{ span: 13 }}>
                      <Search
                        placeholder="Search doc by keyword"
                        allowClear
                        size="small"
                        onSearch={handlePatentSearchFieldChange}
                        style={{ width: 215, height: 20 }}
                      />
                    </Form.Item> */}
                  </Form>
                </div>
                <Spin tip="Loading" spinning={isDirectPatentLoading} size='small'>
                  <ChartUnit title="Assignee" isTemporalVisible={isPatentTemporalVisible} toggleTemporalView={togglePatentBarOrTempView} chart={patentBarChart} />
                </Spin>
              </div>
              <div className="clinical-trial-part">
                <SubTitle title="Clinical Trial" />
                <div className="search-part">
                  <Form
                    size="middle"
                    labelAlign="left"
                    layout="horizontal"
                    style={{ maxWidth: 300 }}
                  >
                    <div className="field-type-div">Topic Level Attributes: Mesh Term</div>
                    {/* <Form.Item label="Impact Explorer Attributes" labelCol={{ span: 15 }} wrapperCol={{ span: 6 }}>
                      <Select
                        defaultValue={"mesh_term"}
                        style={{ width: 125, height: 25 }}
                        onChange={handleClinicalTrialImpactExploreAttrChange}
                        options={[
                          {
                            value: "mesh_term",
                            label: "mesh_term",
                          },
                          {
                            value: "test",
                            label: "test",
                          }
                        ]}
                      />
                    </Form.Item> */}
                    {/* <Form.Item label="Search Field" labelCol={{ span: 8 }} wrapperCol={{ span: 13 }}>
                      <Search
                        placeholder="Search doc by keyword"
                        allowClear
                        size="small"
                        onSearch={handleClinicalTrialSearchFieldChange}
                        style={{ width: 215, height: 20 }}
                      />
                    </Form.Item> */}
                  </Form>
                </div>
                <Spin tip="Loading" spinning={isDirectClinicalTrialLoading} size='small'>
                  <ChartUnit title="Phase" isTemporalVisible={isClinicalTrialTemporalVisible} toggleTemporalView={toggleClinicalTrialBarOrTempView} chart={clinicalTrialPhaseBarChart} />
                  <ChartUnit title="Type" isTemporalVisible={isClinicalTrialTypeTemporalVisible} toggleTemporalView={toggleClinicalTrialTypeBarOrTempView} chart={clinicalTrialTypeBarChart} />
                  {/* <div className="chart-part">
                      <div className="title-switch-part">
                          <div className="title">Phase</div>
                          <div className="temporal-switch">
                            <div className="temporal-text">Temporal</div>
                            <Switch checked={isClinicalTrialTemporalVisible} size="small" onChange={toggleClinicalTrialBarOrTempView} />
                          </div>
                      </div>
                      <div style={{ position: 'relative' }}>
                            {isClinicalTrialTemporalVisible ? (
                              <div className="temporal-view" style={{ position: 'absolute', height: "80px", width: "324px"}}>
                                This is the Temporal View.
                              </div>
                            ) : (
                              <div className="barchart-view" style={{ position: 'absolute', height: "80px", width: "324px" }}>
                                {clinicalTrialPhaseBarChart}
                              </div>
                            )}
                      </div>
                    </div> */}
                    {/* <div className="chart-part">
                      <div className="title-switch-part">
                        <div className="title">Type</div>
                        <div className="temporal-switch">
                          <div className="temporal-text">Temporal</div>
                          <Switch checked={isClinicalTrialTypeTemporalVisible} size="small" onChange={toggleClinicalTrialTypeBarOrTempView} />
                        </div>
                      </div>
                      <div style={{ position: 'relative' }}>
                          {isClinicalTrialTypeTemporalVisible ? (
                            <div className="temporal-view" style={{ position: 'absolute', height: "80px", width: "324px"}}>

                              This is the Temporal View.
                            </div>
                          ) : (
                            <div className="barchart-view" style={{ position: 'absolute', height: "80px", width: "324px" }}>


                              {clinicalTrialTypeBarChart}
                            </div>
                          )}
                      </div>
                    </div> */}
                </Spin>
              </div>
            </div>
            <div className="indirect-impact-entity-subview" style={{display: isIndirectImpactDisplay}}>
              <div className="indirect-patent-part">
                <SubTitle title="Patent" />
                <div className="search-part">
                  <Form
                    size="middle"
                    labelAlign="left"
                    layout="horizontal"
                    style={{ maxWidth: 300 }}
                  >
                    <div className="field-type-div">Topic Level Attributes: CPC Category</div>
                    {/* <Form.Item label="Impact Explorer Attributes" labelCol={{ span: 15 }} wrapperCol={{ span: 6 }}>
                      <Select
                        defaultValue="CPC Category"
                        style={{ width: 125, height: 25 }}
                        onChange={handleIndirectPatentImpactExploreAttrChange}
                        options={[
                          {
                            value: 'CPC Category',
                            label: 'CPC Category',
                          },
                          {
                            value: 'Test',
                            label: 'Test',
                          }
                        ]}
                      />
                    </Form.Item> */}
                    {/* <Form.Item label="Search Field" labelCol={{ span: 8 }} wrapperCol={{ span: 13 }}>
                      <Search
                        placeholder="Search doc by keyword"
                        allowClear
                        size="small"
                        onSearch={handleIndirectPatentSearchFieldChange}
                        style={{ width: 215, height: 20 }}
                      />
                    </Form.Item> */}
                  </Form>
                </div>
                <Spin tip="Loading" spinning={isIndirectPatentLoading} size='small'>
                  <ChartUnit title="Assignee" isTemporalVisible={isIndirectPatentTemporalVisible} toggleTemporalView={toggleIndirectPatentBarOrTempView} chart={indirectPatentBarChart} />
                  {/* <div className="chart-part">
                    <div className="title-switch-part">
                      <div className="title">Assignee</div>
                      <div className="temporal-switch">
                        <div className="temporal-text">Temporal</div>
                        <Switch checked={isIndirectPatentTemporalVisible} size="small" onChange={toggleIndirectPatentBarOrTempView} />
                      </div>
                    </div>
                      <div style={{ position: 'relative' }}>
                        {isIndirectPatentTemporalVisible ? (
                          <div className="temporal-view" style={{ position: 'absolute', height: "80px", width: "324px"}}>
                            This is the Temporal View.
                          </div>
                        ) : (
                          <div className="barchart-view" style={{ position: 'absolute', height: "80px", width: "324px" }}>
                            {indirectPatentBarChart}
                          </div>
                        )}
                      </div>
                  </div> */}
                </Spin>
              </div>
              <div className="indirect-clinical-trial-part">
                <SubTitle title="Clinical Trial" />
                <div className="search-part">
                  <Form
                    size="middle"
                    labelAlign="left"
                    layout="horizontal"
                    style={{ maxWidth: 300 }}
                  >
                    <div className="field-type-div">Topic Level Attributes: Mesh Term</div>
                    {/* <Form.Item label="Impact Explorer Attributes" labelCol={{ span: 15 }} wrapperCol={{ span: 6 }}>
                      <Select
                        defaultValue={"mesh_term"}
                        style={{ width: 125, height: 25 }}
                        onChange={handleIndirectClinicalTrialImpactExploreAttrChange}
                        options={[
                          {
                            value: "mesh_term",
                            label: "mesh_term",
                          },
                          {
                            value: "test",
                            label: "test",
                          }
                        ]}
                      />
                    </Form.Item> */}
                    {/* <Form.Item label="Search Field" labelCol={{ span: 8 }} wrapperCol={{ span: 13 }}>
                      <Search
                        placeholder="Search doc by keyword"
                        allowClear
                        size="small"
                        onSearch={handleIndirectClinicalTrialSearchFieldChange}
                        style={{ width: 215, height: 20 }}
                      />
                    </Form.Item> */}
                  </Form>
                </div>
                <Spin tip="Loading" spinning={isIndirectClinicalTrialLoading} size='small'>
                  <ChartUnit title="Phase" isTemporalVisible={isIndirectClinicalTrialTemporalVisible} toggleTemporalView={toggleIndirectClinicalTrialBarOrTempView} chart={indirectClinicalTrialPhaseBarChart} />
                  <ChartUnit title="Type" isTemporalVisible={isIndirectClinicalTrialTypeTemporalVisible} toggleTemporalView={toggleIndirectClinicalTrialTypeBarOrTempView} chart={indirectClinicalTrialTypeBarChart} />
                  {/* <div className="chart-part">
                      <div className="title-switch-part">
                          <div className="title">Phase</div>
                          <div className="temporal-switch">
                            <div className="temporal-text">Temporal</div>
                            <Switch checked={isIndirectClinicalTrialTemporalVisible} size="small" onChange={toggleIndirectClinicalTrialBarOrTempView} />
                          </div>
                      </div>
                      <div style={{ position: 'relative' }}>
                            {isIndirectClinicalTrialTemporalVisible ? (
                              <div className="temporal-view" style={{ position: 'absolute', height: "80px", width: "324px"}}>
                                This is the Temporal View.
                              </div>
                            ) : (
                              <div className="barchart-view" style={{ position: 'absolute', height: "80px", width: "324px" }}>
                                {indirectClinicalTrialPhaseBarChart}
                              </div>
                            )}
                      </div>
                    </div> */}
                    {/* <div className="chart-part">
                      <div className="title-switch-part">
                        <div className="title">Type</div>
                        <div className="temporal-switch">
                          <div className="temporal-text">Temporal</div>
                          <Switch checked={isIndirectClinicalTrialTypeTemporalVisible} size="small" onChange={toggleIndirectClinicalTrialTypeBarOrTempView} />
                        </div>
                      </div>
                      <div style={{ position: 'relative' }}>
                          {isIndirectClinicalTrialTypeTemporalVisible ? (
                            <div className="temporal-view" style={{ position: 'absolute', height: "80px", width: "324px"}}>
                              This is the Temporal View.
                            </div>
                          ) : (
                            <div className="barchart-view" style={{ position: 'absolute', height: "80px", width: "324px" }}>
                              {indirectClinicalTrialTypeBarChart}
                            </div>
                          )}
                      </div>
                    </div> */}
                </Spin>
              </div>
              <div className="policy-part">
                <SubTitle title="Policy" />
                <div className="search-part">
                  <Form
                    size="middle"
                    labelAlign="left"
                    layout="horizontal"
                    style={{ maxWidth: 300 }}
                  >
                    <div className="field-type-div">Topic Level Attributes: Policy Field</div>
                    {/* <Form.Item label="Impact Explorer Attributes" labelCol={{ span: 15 }} wrapperCol={{ span: 6 }}>
                      <Select
                        defaultValue="Policy Field"
                        style={{ width: 125, height: 25 }}
                        onChange={handlePolicyImpactExploreAttrChange}
                        options={[
                          {
                            value: 'Policy Field',
                            label: 'Policy Field',
                          },
                          {
                            value: 'Test',
                            label: 'Test',
                          }
                        ]}
                      />
                    </Form.Item> */}
                    {/* <Form.Item label="Search Field" labelCol={{ span: 8 }} wrapperCol={{ span: 13 }}>
                      <Search
                        placeholder="Search doc by keyword"
                        allowClear
                        size="small"
                        onSearch={handlePolicySearchFieldChange}
                        style={{ width: 215, height: 20 }}
                      />
                    </Form.Item> */}
                  </Form>
                </div>
                <Spin tip="Loading" spinning={isIndirectPolicyLoading} size='small'>
                  <ChartUnit title="policy_source_type" isTemporalVisible={isPolicyTemporalVisible} toggleTemporalView={togglePolicyBarOrTempView} chart={policyBarChart} />
                  <ChartUnit title="policy_source_country" isTemporalVisible={isPolicySourceCountryTemporalVisible} toggleTemporalView={togglePolicySourceCountryBarOrTempView} chart={policySourceCountryBarChart} />
                  <ChartUnit title="policy_source_title" isTemporalVisible={isPolicySourceTitleTemporalVisible} toggleTemporalView={togglePolicySourceTitleBarOrTempView} chart={policySourceTitleBarChart} />
                  {/* <div className="chart-part">
                    <div className="title-switch-part">
                      <div className="title">policy_source_type</div>
                      <div className="temporal-switch">
                        <div className="temporal-text">Temporal</div>
                        <Switch checked={isPolicyTemporalVisible} size="small" onChange={togglePolicyBarOrTempView} />
                      </div>
                    </div>
                      <div style={{ position: 'relative' }}>
                        {isPolicyTemporalVisible ? (
                          <div className="temporal-view" style={{ position: 'absolute', height: "80px", width: "324px"}}>
                            This is the Temporal View.
                          </div>
                        ) : (
                          <div className="barchart-view" style={{ position: 'absolute', height: "80px", width: "324px" }}>
                            {policyBarChart}
                          </div>
                        )}
                      </div>
                  </div> */}
                  {/* <div className="chart-part">
                    <div className="title-switch-part">
                      <div className="title">policy_source_country</div>
                      <div className="temporal-switch">
                        <div className="temporal-text">Temporal</div>
                        <Switch checked={isPolicySourceCountryTemporalVisible} size="small" onChange={togglePolicySourceCountryBarOrTempView} />
                      </div>
                    </div>
                      <div style={{ position: 'relative' }}>
                        {isPolicySourceCountryTemporalVisible ? (
                          <div className="temporal-view" style={{ position: 'absolute', height: "80px", width: "324px"}}>
                            This is the Temporal View.
                          </div>
                        ) : (
                          <div className="barchart-view" style={{ position: 'absolute', height: "80px", width: "324px" }}>
                            {policySourceCountryBarChart}
                          </div>
                        )}
                      </div>
                  </div> */}
                  {/* <div className="chart-part">
                    <div className="title-switch-part">
                      <div className="title">policy_source_title</div>
                      <div className="temporal-switch">
                        <div className="temporal-text">Temporal</div>
                        <Switch checked={isPolicySourceTitleTemporalVisible} size="small" onChange={togglePolicySourceTitleBarOrTempView} />
                      </div>
                    </div>
                      <div style={{ position: 'relative' }}>
                        {isPolicySourceTitleTemporalVisible ? (
                          <div className="temporal-view" style={{ position: 'absolute', height: "80px", width: "324px"}}>
                            This is the Temporal View.
                          </div>
                        ) : (
                          <div className="barchart-view" style={{ position: 'absolute', height: "80px", width: "324px" }}>
                            {policySourceTitleBarChart}
                          </div>
                        )}
                      </div>
                  </div> */}
                </Spin>
              </div>
              <div className="newsfeed-part">
                <SubTitle title="Newsfeed" />
                <div className="search-part">
                  <Form
                    size="middle"
                    labelAlign="left"
                    layout="horizontal"
                    style={{ maxWidth: 300 }}
                  >
                    <div className="field-type-div">Topic Level Attributes: Outlet or Author</div>
                    {/* <Form.Item label="Impact Explorer Attributes" labelCol={{ span: 15 }} wrapperCol={{ span: 6 }}>
                      <Select
                        defaultValue="Country > Outlet or Author"
                        style={{ width: 125, height: 25 }}
                        onChange={handleNewsfeedImpactExploreAttrChange}
                        options={[
                          {
                            value: 'Country > Outlet or Authord',
                            label: 'Country > Outlet or Author',
                          },
                          {
                            value: 'Test',
                            label: 'Test',
                          }
                        ]}
                      />
                    </Form.Item> */}
                    {/* <Form.Item label="Search Field" labelCol={{ span: 8 }} wrapperCol={{ span: 13 }}>
                      <Search
                        placeholder="Search doc by keyword"
                        allowClear
                        size="small"
                        onSearch={handleNewsfeedSearchFieldChange}
                        style={{ width: 215, height: 20 }}
                      />
                    </Form.Item> */}
                  </Form>
                </div>
                <Spin tip="Loading" spinning={isIndirectNewsfeedLoading} size='small'>
                  <ChartUnit title="Country" isTemporalVisible={isNewsfeedTemporalVisible} toggleTemporalView={toggleNewsfeedBarOrTempView} chart={newsfeedBarChart} />
                  {/* <div className="chart-part">
                    <div className="title-switch-part">
                      <div className="title">Country</div>
                      <div className="temporal-switch">
                        <div className="temporal-text">Temporal</div>
                        <Switch checked={isNewsfeedTemporalVisible} size="small" onChange={toggleNewsfeedBarOrTempView} />
                      </div>
                    </div>
                      <div style={{ position: 'relative' }}>
                        {isNewsfeedTemporalVisible ? (
                          <div className="temporal-view" style={{ position: 'absolute', height: "80px", width: "324px"}}>
                            This is the Temporal View.
                          </div>
                        ) : (
                          <div className="barchart-view" style={{ position: 'absolute', height: "80px", width: "324px" }}>
                            {newsfeedBarChart}
                          </div>
                        )}
                      </div>
                  </div> */}
                </Spin>
              </div>
            </div>
          </div>
      </Spin>
      </ConfigProvider>
    </Panel>
  );
};

export default ImpactEntityView;
